import styled from 'styled-components';
import { useCallback, useEffect, useState, useRef } from 'react';
import logo from 'assets/images/Logo.svg';
import getLocalizedNews from 'components/news/getLanguageData';
import { getCurrentDateTime, getWelcomeNoteData } from '../common/utils';
import { DateText, TimeText } from '../common/styles';
import EventBus from 'utils/eventBus';

const DEFAULT_ANIMATION_DURATION = 15; // Default duration in seconds

export default function Header() {
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [_, setCurrentNoteIndex] = useState(0);
  const [newsItems, setNewsItems] = useState([]);
  const [dateTime, setDateTime] = useState({ date: '', time: '' });
  const [welcomeNoteData, setWelcomeNoteData] = useState([]);
  const [welcomeNoteDisplay, setWelcomeNoteDisplay] = useState('');
  const sliderRef = useRef([]);
  const [animationDurations, setAnimationDurations] = useState([
    DEFAULT_ANIMATION_DURATION,
    DEFAULT_ANIMATION_DURATION,
  ]); // Default durations for news and welcome notes

  const handleAnimationEnd = type => {
    if (type === 'news') {
      setCurrentNewsIndex(prevIndex => (prevIndex + 1) % newsItems.length);
    } else if (type === 'welcome') {
      createWelcomeNote();
    }
  };

  const getData = () => {
    const newsData = getLocalizedNews();
    welcomeNoteHandeling();
    const localizedNews = newsData?.languageData.map(item => ({
      tite: item?.latest_news,
      // caption: item?.locale_latest_news?.caption,
      description: item?.locale_latest_news?.locale_description,
    }));
    setNewsItems(localizedNews.map(news => `${news.description}`));
  };

  useEffect(() => {
    const onRefreshSub = EventBus.subscribe('refresh', () => {
      getData();
    });
    getData();

    const getTime = async () => {
      setDateTime(await getCurrentDateTime());
    };
    getTime();
    const timeInterval = setInterval(() => {
      getTime();
    }, 10000 * 6);

    return () => {
      clearInterval(timeInterval);
      onRefreshSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const calculateDurations = () => {
      const durations = sliderRef.current.map(slider => {
        if (!slider) return DEFAULT_ANIMATION_DURATION; // Default duration
        const textWidth = slider.scrollWidth; // Width of the text
        const containerWidth = slider.clientWidth; // Width of the container
        const speed = 50; // Pixels per second
        return Math.max((textWidth - containerWidth) / speed, DEFAULT_ANIMATION_DURATION); // Minimum duration of 10s
      });
      setAnimationDurations(durations);
    };

    calculateDurations();
    window.addEventListener('resize', calculateDurations);
    return () => window.removeEventListener('resize', calculateDurations);
  }, [newsItems, welcomeNoteDisplay]);

  const createWelcomeNote = useCallback(() => {
    setCurrentNoteIndex(prevIndex => {
      if (welcomeNoteData.length === 0) return prevIndex;
      const newIndex = (prevIndex + 1) % welcomeNoteData.length;
      const note = `${welcomeNoteData[newIndex]?.locale_welcome_note?.caption || ''} : ${welcomeNoteData[newIndex]?.locale_welcome_note?.description || ''}`;
      setWelcomeNoteDisplay(note);
      return newIndex;
    });
  }, [welcomeNoteData]);

  const welcomeNoteHandeling = () => {
    const locale_note = getWelcomeNoteData();
    if (locale_note !== null && locale_note.length) {
      setWelcomeNoteData(locale_note);
      // Immediately display the first welcome note!
      if (locale_note.length > 0) {
        const firstNote = `${locale_note[0]?.locale_welcome_note?.caption || ''} : ${locale_note[0]?.locale_welcome_note?.description || ''}`;
        setWelcomeNoteDisplay(firstNote);
        setCurrentNoteIndex(0);
      }
    } else {
      setWelcomeNoteData([]);
      setWelcomeNoteDisplay(''); // Clear if no data
      setCurrentNoteIndex(0);
    }
  };

  return (
    <HeaderInner>
      <Logo />
      <SliderContainder>
        <DataSlider
          ref={el => (sliderRef.current[0] = el)}
          duration={animationDurations[0]}
          onAnimationIteration={() => handleAnimationEnd('news')}
        >
          <span key={newsItems[currentNewsIndex]}>{newsItems[currentNewsIndex]}</span>
        </DataSlider>
        <DataSlider
          ref={el => (sliderRef.current[1] = el)}
          duration={animationDurations[1]}
          onAnimationIteration={() => handleAnimationEnd('welcome')}
        >
          <span key={welcomeNoteDisplay}>{welcomeNoteDisplay}</span>
        </DataSlider>
      </SliderContainder>
      <TimeContainer>
        <TimeText>{dateTime.time}</TimeText>
        <DateText>{dateTime.date}</DateText>
      </TimeContainer>
    </HeaderInner>
  );
}

const HeaderInner = styled.div`
  top: 5%;
  position: absolute;
  left: 25px;
  display: flex;
  justify-content: space-between;
  width: calc(100% - 50px);
  color: ${props => props.theme.colors.textPrimary};
  align-items: center;
  right: 25px;
`;

const LeftRightDivs = styled.div`
  height: 100px;
`;

const Logo = styled(LeftRightDivs)`
  font-weight: bold;
  background: url(${logo}) no-repeat center center;
  background-position: left;
  width: 145px;
`;

const TimeContainer = styled(LeftRightDivs)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 10px;
`;

const SliderContainder = styled.div`
  flex: 1;
  max-width: calc(100% - 600px);
`;

const DataSlider = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.theme.colors.textPrimary};
  background: ${props => props.theme.colors.containerBackground};
  padding: 5px 20px;
  border-radius: 10px;
  margin: 8px 20px;
  overflow: hidden;
  white-space: nowrap;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 20px;
    height: 100%;
    background: linear-gradient(
      to left,
      ${props => props.theme.colors.containerBackground},
      transparent
    );
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 20px;
    height: 100%;
    background: linear-gradient(
      to right,
      ${props => props.theme.colors.containerBackground},
      transparent
    );
  }

  & > span {
    display: inline-block;
    padding-left: 100%;
    animation: slideText ${props => props.duration}s linear infinite;
    animation-delay: -${props => props.duration / 2}s; /* Start the animation halfway through */
  }

  @keyframes slideText {
    from {
      transform: translateX(105%); /* Start completely off-screen to the right */
    }
    to {
      transform: translateX(-105%); /* End completely off-screen to the left */
    }
  }
`;
